import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
import useFlag from 'hooks/useFlag'
import { useEffect } from 'react'

// Ortto tracking ("ap3c") snippet. Replaces the Delighted NPS embed ahead of
// the Delighted sunset on 2026-07-01. Faithful to the vendor snippet Ortto
// generated for the Thanx account: a command queue (`ap3c.cmd`) drained by the
// loader, with init + track pushed onto it. The capture key is a public
// client-side key (like the Pendo agent key), injected at build time via env.
// Docs: https://help.ortto.com/tracking-code
const ORTTO_SCRIPT_ID = 'ortto-ap3c-embed-script'
const ORTTO_SCRIPT_SRC = 'https://cdn3l.ink/app.js'
const ORTTO_CAPTURE_API = 'https://capture-api-us.ortto.app/'

type Ap3cCommand = () => void

type Ap3c = {
  cmd?: Ap3cCommand[]
  init?: (captureKey: string, endpoint: string) => void
  track?: (person: Record<string, unknown>) => void
  activity?: (act: unknown) => void
  act?: unknown[]
}

declare global {
  interface Window {
    ap3c?: Ap3c
  }
}

const Ortto = () => {
  // Gated behind a permanent LaunchDarkly kill switch. Defaults to false so the
  // embed fails safe (stays dark) if LD can't load — the flag is the only way
  // to disable this third-party script in an emergency without a redeploy.
  const isOrttoNpsEnabled = useFlag('ortto-nps-embed', false)
  const captureKey = process.env.REACT_APP_ORTTO_CAPTURE_KEY
  const merchantUser = useCurrentMerchantUser()

  // Set up the ap3c command queue and inject the loader once. Commands pushed
  // onto ap3c.cmd run after the loader script drains the queue, so order and
  // timing relative to load do not matter.
  useEffect(() => {
    if (!isOrttoNpsEnabled || !captureKey) {
      return
    }

    const ap3c: Ap3c = (window.ap3c = window.ap3c || {})
    ap3c.cmd = ap3c.cmd || []
    ap3c.cmd.push(() => {
      window.ap3c?.init?.(captureKey, ORTTO_CAPTURE_API)
    })
    ap3c.activity =
      ap3c.activity ||
      function (act: unknown) {
        ap3c.act = ap3c.act || []
        ap3c.act.push(act)
      }

    if (!document.getElementById(ORTTO_SCRIPT_ID)) {
      const script = document.createElement('script')
      script.id = ORTTO_SCRIPT_ID
      script.type = 'text/javascript'
      script.async = true
      script.src = ORTTO_SCRIPT_SRC
      document.head.appendChild(script)
    }
  }, [isOrttoNpsEnabled, captureKey])

  // Destructure to primitives so the identify effect depends on stable values,
  // not the Redux-ORM merchantUser instance (re-created on every ORM change).
  const email = merchantUser?.email
  const firstName = merchantUser?.first_name
  const lastName = merchantUser?.last_name

  // Identify the logged-in operator by email. skipNonExisting prevents creating
  // net-new Ortto Persons for dashboard users who aren't synced from Salesforce;
  // only existing Persons (the DM/PO/ES stakeholders) get associated. Decision
  // confirmed by Dean 2026-07-10 (email over Salesforce Contact Id).
  useEffect(() => {
    if (!isOrttoNpsEnabled || !captureKey || !email) {
      return
    }

    window.ap3c?.cmd?.push(() => {
      window.ap3c?.track?.({
        v: 0,
        email,
        first: firstName,
        last: lastName,
        skipNonExisting: true,
      })
    })
  }, [isOrttoNpsEnabled, captureKey, email, firstName, lastName])

  return null
}

export default Ortto
